import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Lock, Loader2, ArrowLeft, ClipboardList, Search, ExternalLink, Gauge } from "lucide-react";
import Header from "@/components/quote/Header";
import { adminLogin, getAdminQuotes, getAdminUsage } from "@/lib/api";

const PC_KEY = "gg_admin_pc";
const usd = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number.isFinite(+v) ? +v : 0);
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—");

export default function SalesDashboard() {
  const navigate = useNavigate();
  const [passcode, setPasscode] = useState(sessionStorage.getItem(PC_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [usage, setUsage] = useState([]);
  const [q, setQ] = useState("");

  const load = async (pc) => {
    const d = await getAdminQuotes(pc);
    setQuotes(d.quotes);
    try {
      const u = await getAdminUsage(pc);
      setUsage(u.usage);
    } catch (e) {
      console.warn("Sales usage load failed", e);
    }
  };

  useEffect(() => {
    document.title = "GloGrid VMI Quoter · Sales";
    const stored = sessionStorage.getItem(PC_KEY);
    if (stored) {
      load(stored)
        .then(() => setAuthed(true))
        .catch(() => {
          sessionStorage.removeItem(PC_KEY);
          setPasscode("");
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogin = async (e) => {
    e.preventDefault();
    setChecking(true);
    try {
      await adminLogin(passcode);
      sessionStorage.setItem(PC_KEY, passcode);
      await load(passcode);
      setAuthed(true);
    } catch (err) {
      toast.error("Invalid passcode");
    } finally {
      setChecking(false);
    }
  };

  const filtered = quotes.filter((x) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [x.reference, x.companyName, x.contactName, x.contactEmail, x.location]
      .filter(Boolean)
      .some((v) => `${v}`.toLowerCase().includes(s));
  });

  if (!authed) {
    return (
      <div className="min-h-screen bg-slate-100">
        <Header />
        <main className="mx-auto flex max-w-md flex-col px-6 py-20">
          <div className="rounded-sm border border-slate-200 bg-white p-8 shadow-sm gg-rise">
            <div className="grid h-11 w-11 place-items-center rounded-sm bg-slate-900 text-white">
              <Lock className="h-5 w-5" />
            </div>
            <h1 className="mt-5 font-heading text-2xl font-extrabold tracking-tight text-slate-900">Sales Dashboard</h1>
            <p className="mt-2 text-sm text-slate-500">Enter the shared admin passcode to view saved quotes.</p>
            <form onSubmit={onLogin} className="mt-6 flex flex-col gap-3">
              <Label htmlFor="pc" className="text-xs font-semibold uppercase tracking-wider text-slate-500">Passcode</Label>
              <Input
                id="pc"
                type="password"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="••••••••"
                className="h-12 rounded-sm border-slate-300 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                data-testid="sales-passcode-input"
              />
              <Button
                type="submit"
                disabled={checking || !passcode}
                className="h-12 rounded-sm bg-slate-900 font-semibold text-white transition-colors hover:bg-blue-600 disabled:opacity-50"
                data-testid="sales-login-btn"
              >
                {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}
              </Button>
            </form>
          </div>
          <button
            onClick={() => navigate("/")}
            className="mt-6 inline-flex items-center gap-1.5 self-start text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" /> Back to calculator
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <Header />
      <main className="mx-auto max-w-6xl px-6 py-10 md:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-600">
              <ClipboardList className="h-4 w-4" /> Sales
            </div>
            <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900">Saved Quotes</h1>
            <p className="mt-1 text-sm text-slate-500">{quotes.length} quotes · newest first</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search company, email, ref…"
                className="h-10 w-64 rounded-sm border-slate-300 bg-white pl-9 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                data-testid="sales-search"
              />
            </div>
            <Link to="/admin" className="text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="sales-to-admin">
              Rate Admin →
            </Link>
          </div>
        </div>

        {/* Credit usage per user */}
        <section className="mt-8" data-testid="usage-panel">
          <div className="mb-3 flex items-center gap-2.5">
            <span className="grid h-7 w-7 place-items-center rounded-sm bg-slate-900 text-white">
              <Gauge className="h-4 w-4" />
            </span>
            <h2 className="font-heading text-xl font-extrabold tracking-tight text-slate-900">Credit Usage by User</h2>
            <span className="text-sm text-slate-400">· estimates consumed</span>
          </div>
          <div className="overflow-x-auto rounded-sm border border-slate-200 bg-white shadow-sm">
            <table className="w-full min-w-[640px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left">
                  {["User email", "This month", "Free limit", "Remaining", "All-time"].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {usage.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-400">No estimate usage yet.</td></tr>
                ) : (
                  usage.map((u) => (
                    <tr key={u.email} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`usage-row-${u.email}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {u.email}
                        {u.unlimited && (
                          <span className="ml-2 rounded-sm bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-blue-700">Owner</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono-plex font-semibold text-slate-900">{u.thisMonth}</td>
                      <td className="px-4 py-3 text-slate-600">{u.unlimited ? "∞" : u.limit}</td>
                      <td className="px-4 py-3">
                        {u.unlimited ? (
                          <span className="text-blue-600">Unlimited</span>
                        ) : (
                          <span className={u.remaining === 0 ? "font-semibold text-red-600" : "text-slate-600"}>{u.remaining}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono-plex text-slate-500">{u.total}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <div className="mt-10 mb-3 flex items-center gap-2.5">
          <span className="grid h-7 w-7 place-items-center rounded-sm bg-slate-900 text-white">
            <ClipboardList className="h-4 w-4" />
          </span>
          <h2 className="font-heading text-xl font-extrabold tracking-tight text-slate-900">Saved Quotes</h2>
        </div>

        <div className="mt-2 overflow-x-auto rounded-sm border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[900px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                {["Reference", "Date", "Valid until", "Company", "Contact", "Location", "Total", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-sm text-slate-400">No quotes found.</td>
                </tr>
              ) : (
                filtered.map((x) => {
                  const expired = x.validUntil ? new Date(x.validUntil) < new Date() : false;
                  return (
                    <tr key={x.reference} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`sales-row-${x.reference}`}>
                      <td className="px-4 py-3 font-mono-plex font-semibold text-slate-900">{x.reference}</td>
                      <td className="px-4 py-3 text-slate-600">{fmt(x.createdAt)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 ${expired ? "text-red-600" : "text-slate-600"}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${expired ? "bg-red-500" : "bg-green-500"}`} />
                          {fmt(x.validUntil)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-800">{x.companyName || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <div>{x.contactName || "—"}</div>
                        {x.contactEmail && <div className="text-xs text-slate-400">{x.contactEmail}</div>}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{x.location || "—"}</td>
                      <td className="px-4 py-3 font-mono-plex font-semibold text-slate-900">{usd(x.total)}</td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/quote/${x.reference}`}
                          className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700"
                          data-testid={`sales-open-${x.reference}`}
                        >
                          Open <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
